/**
 * The Resources "All" list of a room: the items of its sections, its member
 * media, its podcasts and, on the phone, its congress spaces, as one list
 * (docs/room-sections.md, "The "All" list"). The API builds it; the webapp
 * names its items with the same key.
 *
 * Generic over the cards: the API holds them with `Date` fields, the webapp
 * holds the same cards as JSON.
 */
type Card = { id: string };

export type RoomResourceItem<
  A extends Card,
  C extends Card,
  P extends Card,
  D extends Card,
> =
  | { kind: "article"; article: A }
  | { kind: "content"; content: C }
  | { kind: "playlist"; playlist: P }
  | { kind: "podcast"; podcast: D }
  | { kind: "congress"; spaceId: string };

type AnyRoomResourceItem = RoomResourceItem<Card, Card, Card, Card>;

/** One item's identity in the list: its kind and its id. */
export function resourceItemKey(item: AnyRoomResourceItem): string {
  switch (item.kind) {
    case "article":
      return `article-${item.article.id}`;
    case "content":
      return `content-${item.content.id}`;
    case "playlist":
      return `playlist-${item.playlist.id}`;
    case "podcast":
      return `podcast-${item.podcast.id}`;
    case "congress":
      return `congress-${item.spaceId}`;
    default:
      throw new Error(item satisfies never);
  }
}

/** An item of a section, the member media or the podcasts: never a congress. */
type MergedItem = Exclude<AnyRoomResourceItem, { kind: "congress" }> & {
  pinned: boolean;
  sortDate: number;
};

/**
 * Drops repeats and orders the result by today's rule, which a room without a
 * manual order reads (domain/room-resources-order.ts): pinned first, then the
 * newest `sortDate`. An item can sit in several sections, and a section's content can
 * also be a room member: the first copy is kept, and it is pinned if ANY copy
 * is, because CX can reorder the sections and that must not pin or unpin an
 * item nobody touched. The sort is stable, so items with the same pin and date
 * keep the order of `items`: the caller's merge order is the last key.
 */
export function mergeRoomResourceItems<T extends MergedItem>(
  items: readonly T[],
): T[] {
  const indexOf = new Map<string, number>();
  const deduped: T[] = [];
  for (const item of items) {
    const key = resourceItemKey(item);
    const index = indexOf.get(key);
    if (index === undefined) {
      indexOf.set(key, deduped.length);
      deduped.push(item);
      continue;
    }
    const kept = deduped[index];
    if (kept === undefined)
      throw new Error(`should not happen: no item at index ${index}`);
    if (item.pinned && !kept.pinned) deduped[index] = { ...kept, pinned: true };
  }
  return deduped.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.sortDate - a.sortDate;
  });
}

/**
 * One podcast of the room, as "All" shows it in one language: its episodes
 * published in that language, and the entry that stands for the podcast when
 * it has exactly one (`single`, that episode), else `null`.
 */
type RoomPodcastSeries<T> = {
  podcastId: string;
  episodeIds: readonly string[];
  single: T | null;
};

/**
 * One entry per podcast under "All", never the series and its episode
 * together (docs/room-sections.md, "One entry per podcast"):
 *
 * - one episode in the language: that episode, at the higher of the places the
 *   series card and the episode held;
 * - two or more: the series card, and none of its episodes;
 * - none: the series card, as before.
 *
 * An episode whose series the room does not show keeps its place. An episode
 * two series share belongs to the one whose card comes first in `items` (when
 * neither card is there, the first in `series`), and shows once, as that
 * series' entry. A series whose only episode another series holds shows its
 * series card, as before: no series leaves "All". `items` is in its final
 * order; the result keeps that order.
 */
export function onePodcastEntry<T extends AnyRoomResourceItem>(
  items: readonly T[],
  series: readonly RoomPodcastSeries<T>[],
): T[] {
  const seriesById = new Map(series.map((entry) => [entry.podcastId, entry]));
  const cardIndex = new Map(
    items.flatMap((item, index) =>
      item.kind === "podcast" ? [[item.podcast.id, index] as const] : [],
    ),
  );
  const byCardPlace = series
    .map((entry, order) => {
      const index = cardIndex.get(entry.podcastId);
      return { entry, order, index: index === undefined ? null : index };
    })
    .sort((a, b) => {
      if (a.index !== b.index) {
        if (a.index === null) return 1;
        if (b.index === null) return -1;
        return a.index - b.index;
      }
      return a.order - b.order;
    });
  const seriesOfEpisode = new Map<string, RoomPodcastSeries<T>>();
  for (const { entry } of byCardPlace)
    for (const episodeId of entry.episodeIds)
      if (!seriesOfEpisode.has(episodeId))
        seriesOfEpisode.set(episodeId, entry);
  const placed = new Set<string>();
  return items.flatMap((item) => {
    const entry =
      item.kind === "podcast"
        ? seriesById.get(item.podcast.id)
        : item.kind === "content"
          ? seriesOfEpisode.get(item.content.id)
          : undefined;
    if (entry === undefined) return [item];
    if (entry.single === null) return item.kind === "podcast" ? [item] : [];
    // Its only episode belongs to another series, which shows it: this one
    // keeps its series card.
    const [onlyEpisode] = entry.episodeIds;
    if (onlyEpisode === undefined)
      throw new Error(
        `should not happen: podcast ${entry.podcastId} stands in with no episode`,
      );
    if (seriesOfEpisode.get(onlyEpisode) !== entry)
      return item.kind === "podcast" ? [item] : [];
    if (placed.has(entry.podcastId)) return [];
    placed.add(entry.podcastId);
    return [entry.single];
  });
}
